import { Vector2 } from './math'

interface PathNode {
  x: number
  y: number
  g: number
  h: number
  f: number
  parent: PathNode | null
}

export interface PathGrid {
  width: number
  height: number
  isWalkable: (x: number, y: number) => boolean
  getCost?: (x: number, y: number) => number
}

// A* pathfinding with optimization
export function findPath(
  start: Vector2,
  end: Vector2,
  grid: PathGrid,
  maxIterations: number = 1000
): Vector2[] {
  const startX = Math.floor(start.x)
  const startY = Math.floor(start.y)
  const endX = Math.floor(end.x)
  const endY = Math.floor(end.y)

  // Early exit if end is not valid
  if (!grid.isWalkable(endX, endY)) {
    // Find nearest walkable tile to end
    const nearest = findNearestWalkable(endX, endY, grid, 5)
    if (!nearest) return []
    return findPath(start, nearest, grid, maxIterations)
  }

  if (startX === endX && startY === endY) return []

  // If start is not walkable (unit stuck on building tile), find nearest walkable
  // and path from there - the unit will move toward it
  let actualStartX = startX
  let actualStartY = startY
  if (!grid.isWalkable(startX, startY)) {
    const nearestStart = findNearestWalkable(startX, startY, grid, 3)
    if (!nearestStart) return []
    actualStartX = Math.floor(nearestStart.x)
    actualStartY = Math.floor(nearestStart.y)
  }

  const openSet: PathNode[] = []
  const closedSet = new Set<string>()

  const startNode: PathNode = {
    x: actualStartX,
    y: actualStartY,
    g: 0,
    h: heuristic(startX, startY, endX, endY),
    f: 0,
    parent: null
  }
  startNode.f = startNode.g + startNode.h
  openSet.push(startNode)

  let iterations = 0

  while (openSet.length > 0 && iterations < maxIterations) {
    iterations++

    // Get node with lowest f
    openSet.sort((a, b) => a.f - b.f)
    const current = openSet.shift()!

    if (current.x === endX && current.y === endY) {
      return reconstructPath(current)
    }

    closedSet.add(`${current.x},${current.y}`)

    // Check all 8 neighbors (including diagonals)
    const neighbors = [
      { x: current.x - 1, y: current.y, cost: 1 },
      { x: current.x + 1, y: current.y, cost: 1 },
      { x: current.x, y: current.y - 1, cost: 1 },
      { x: current.x, y: current.y + 1, cost: 1 },
      { x: current.x - 1, y: current.y - 1, cost: 1.41 },
      { x: current.x + 1, y: current.y - 1, cost: 1.41 },
      { x: current.x - 1, y: current.y + 1, cost: 1.41 },
      { x: current.x + 1, y: current.y + 1, cost: 1.41 }
    ]

    for (const neighbor of neighbors) {
      const { x, y, cost } = neighbor

      // Bounds check
      if (x < 0 || x >= grid.width || y < 0 || y >= grid.height) continue

      // Walkability check
      if (!grid.isWalkable(x, y)) continue

      // Already processed
      if (closedSet.has(`${x},${y}`)) continue

      // For diagonal movement, check that we can actually move diagonally
      if (cost > 1) {
        const dx = x - current.x
        const dy = y - current.y
        if (!grid.isWalkable(current.x + dx, current.y) ||
            !grid.isWalkable(current.x, current.y + dy)) {
          continue
        }
      }

      const terrainCost = grid.getCost?.(x, y) ?? 1
      const g = current.g + cost * terrainCost
      const h = heuristic(x, y, endX, endY)
      const f = g + h

      // Check if already in open set with better g
      const existing = openSet.find(n => n.x === x && n.y === y)
      if (existing) {
        if (g < existing.g) {
          existing.g = g
          existing.f = f
          existing.parent = current
        }
      } else {
        openSet.push({
          x,
          y,
          g,
          h,
          f,
          parent: current
        })
      }
    }
  }

  return [] // No path found
}

function heuristic(ax: number, ay: number, bx: number, by: number): number {
  // Octile distance for 8-directional movement
  const dx = Math.abs(bx - ax)
  const dy = Math.abs(by - ay)
  return Math.max(dx, dy) + (Math.SQRT2 - 1) * Math.min(dx, dy)
}

function reconstructPath(endNode: PathNode): Vector2[] {
  const path: Vector2[] = []
  let current: PathNode | null = endNode

  while (current) {
    path.unshift({ x: current.x + 0.5, y: current.y + 0.5 })
    current = current.parent
  }

  // Remove start position
  path.shift()

  return path
}

function findNearestWalkable(x: number, y: number, grid: PathGrid, maxRadius: number): Vector2 | null {
  for (let r = 1; r <= maxRadius; r++) {
    for (let dx = -r; dx <= r; dx++) {
      for (let dy = -r; dy <= r; dy++) {
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue
        const nx = x + dx
        const ny = y + dy
        if (nx >= 0 && nx < grid.width && ny >= 0 && ny < grid.height) {
          if (grid.isWalkable(nx, ny)) {
            return { x: nx, y: ny }
          }
        }
      }
    }
  }
  return null
}

// Path smoothing - remove unnecessary waypoints
export function smoothPath(path: Vector2[], grid: PathGrid): Vector2[] {
  if (path.length <= 2) return path

  const smoothed: Vector2[] = [path[0]]
  let current = 0

  while (current < path.length - 1) {
    // Look ahead for the furthest visible point
    let furthest = current + 1
    for (let i = current + 2; i < path.length; i++) {
      if (hasLineOfSight(path[current], path[i], grid)) {
        furthest = i
      }
    }
    smoothed.push(path[furthest])
    current = furthest
  }

  return smoothed
}

function hasLineOfSight(a: Vector2, b: Vector2, grid: PathGrid): boolean {
  const dx = Math.abs(b.x - a.x)
  const dy = Math.abs(b.y - a.y)
  const sx = a.x < b.x ? 1 : -1
  const sy = a.y < b.y ? 1 : -1

  let x = Math.floor(a.x)
  let y = Math.floor(a.y)
  const ex = Math.floor(b.x)
  const ey = Math.floor(b.y)

  let err = dx - dy

  while (true) {
    if (!grid.isWalkable(x, y)) return false
    if (x === ex && y === ey) return true

    const e2 = 2 * err
    if (e2 > -dy) {
      err -= dy
      x += sx
    }
    if (e2 < dx) {
      err += dx
      y += sy
    }
  }
}
